import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Painel } from '../../../../../components/Painel';
import { InputTextArea } from '../../../../../components/InputTextArea';
import { optionProps } from '../../../../../components/Select';
import { SeparateLabel } from '../../../../../components/SeparateLabel';
import { CardMuscleGroup } from '../../../../../components/CardMuscleGroup';
import { TextInfo } from '../../../../../components/TextInfo';

import { useToast } from '../../../../../hooks/toast';
import { getValidationErrors } from '../../../../../utils/getValidationErrors';
import { AdminExercisesService } from '../../../../../services/AdminExercisesService';
import {
  IExercise,
  IExerciseMuscleGroup,
  IMuscleGroup,
} from '../../../../../domains/Exercise';

import {
  Card,
  Container,
  ContainerCardsMuscleGroups,
  ContainerInline,
  DeleteButton,
  Discard,
  FormContainer,
  Grid,
  Header,
  HeaderActions,
  Input,
  PageTitle,
  SelectField,
  Submit,
} from './styles';

interface IFormProps {
  name: string;
  description: string;
}

interface locationState {
  exercise?: IExercise;
}

const ExerciseEditor: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { state } = useLocation();
  const { addToast } = useToast();

  const adminExercisesService = new AdminExercisesService();
  const isEditing = !!id;

  const [exercise, setExercise] = useState<IExercise | undefined>(
    (state as locationState)?.exercise,
  );
  const [muscleGroups, setMuscleGroups] = useState<IMuscleGroup[]>([]);
  const [listMuscleGroups, setListMuscleGroups] = useState<
    IExerciseMuscleGroup[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [loadingExercise, setLoadingExercise] = useState(isEditing);

  const getMuscleGroups = useCallback(() => {
    adminExercisesService
      .getMuscleGroups()
      .then(response => setMuscleGroups(response.data))
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos grupos musculares.',
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast]);

  useEffect(() => {
    getMuscleGroups();
  }, [getMuscleGroups]);

  useEffect(() => {
    if (!isEditing) return;

    if (exercise) {
      setListMuscleGroups(exercise.muscle_groups);
      setLoadingExercise(false);
      return;
    }

    adminExercisesService
      .getExerciseById(id as string)
      .then(response => {
        setExercise(response.data);
        setListMuscleGroups(response.data.muscle_groups);
      })
      .catch(() => {
        addToast({ type: 'error', title: 'Exercício não encontrado.' });
        navigate('/admin/exercises');
      })
      .finally(() => setLoadingExercise(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const mainGroup = listMuscleGroups.find(group => group.is_main);
  const secondaryGroups = listMuscleGroups.filter(group => !group.is_main);

  const addItemMuscleGroup = useCallback(
    (selected: optionProps) => {
      if (
        !listMuscleGroups.find(
          group => group.muscle_group_id === selected.value,
        )
      ) {
        const groupfinded = muscleGroups.find(
          finded => finded.id === selected.value,
        );
        if (groupfinded) {
          setListMuscleGroups(element => [
            ...element,
            {
              name: selected.label,
              muscle_group_id: selected.value,
              is_main: false,
            } as IExerciseMuscleGroup,
          ]);
        }
      }
    },
    [listMuscleGroups, muscleGroups],
  );

  const handleChangeSelect = useCallback(
    (e: any) => {
      const option: optionProps = e;
      if (option) addItemMuscleGroup(option);
    },
    [addItemMuscleGroup],
  );

  const handleSubmit = useCallback(
    async (data: IFormProps) => {
      try {
        setLoading(true);

        if (listMuscleGroups.length < 1) {
          addToast({
            type: 'error',
            title: 'Pelo menos um grupo muscular deve ser cadastrado.',
          });
          setLoading(false);
          return;
        }

        if (!listMuscleGroups.some(m => m.is_main)) {
          addToast({
            type: 'error',
            title: 'Pelo menos um grupo muscular deve ser o principal.',
          });
          setLoading(false);
          return;
        }

        if (listMuscleGroups.filter(m => m.is_main).length > 1) {
          addToast({
            type: 'error',
            title: 'Apenas um grupo muscular pode ser o principal.',
          });
          setLoading(false);
          return;
        }

        formRef.current?.setErrors({});

        const schema = Yup.object({
          name: Yup.string().required('Campo obrigatório'),
          description: Yup.string(),
        });

        await schema.validate(data, { abortEarly: false });

        if (isEditing && exercise) {
          await adminExercisesService.updateExercise(exercise.id, {
            name: data.name,
            muscle_groups: listMuscleGroups,
            description: data.description || undefined,
          });

          addToast({
            type: 'success',
            title: 'Exercício alterado com sucesso.',
          });
        } else {
          await adminExercisesService.createExercise({
            name: data.name,
            muscle_groups: listMuscleGroups,
            description: data.description || undefined,
          });

          addToast({ type: 'success', title: 'Exercício criado com sucesso' });
        }

        setLoading(false);
        navigate('/admin/exercises');
      } catch (err: any) {
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        if (err?.response?.status === 406) {
          formRef.current?.setErrors({
            name: 'Já existe um exercício com esse nome',
          });
          addToast({
            type: 'error',
            title: 'Exercício com o mesmo nome já cadastrado.',
          });
          return;
        }

        if (err?.response?.status === 403) {
          addToast({
            type: 'error',
            title:
              'Este exercício pertence a uma academia e não pode ser editado por aqui.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: isEditing ? 'Erro na atualização' : 'Erro na criação',
          description: 'Tente novamente mais tarde.',
        });
      }
    },
    [addToast, exercise, isEditing, listMuscleGroups, navigate],
  );

  const handleDelete = useCallback(async () => {
    if (!exercise) return;

    const confirmed = window.confirm(
      'Excluir este exercício da biblioteca padrão? Ele será removido das fichas de TODAS as academias que o utilizam hoje. Essa ação não pode ser desfeita.',
    );

    if (!confirmed) return;

    try {
      await adminExercisesService.removeExercise(exercise.id);

      addToast({ type: 'success', title: 'Exercício removido' });
      navigate('/admin/exercises');
    } catch (err: any) {
      if (err?.response?.status === 403) {
        addToast({
          type: 'error',
          title:
            'Este exercício pertence a uma academia e não pode ser removido por aqui.',
        });
        return;
      }

      addToast({
        type: 'error',
        title: 'Erro na remoção do exercício',
        description: 'Tente novamente mais tarde.',
      });
    }
  }, [addToast, exercise, navigate]);

  return (
    <Painel loading={loadingExercise}>
      <Container>
        <Header>
          <PageTitle>
            {isEditing ? exercise?.name || 'Exercício' : 'Novo exercício'}
          </PageTitle>
          <HeaderActions>
            {isEditing && (
              <DeleteButton type="button" onClick={handleDelete}>
                Excluir
              </DeleteButton>
            )}
            <Discard type="button" onClick={() => navigate('/admin/exercises')}>
              Descartar
            </Discard>
          </HeaderActions>
        </Header>

        {(!isEditing || !!exercise) && (
          <FormContainer
            ref={formRef}
            initialData={exercise}
            onSubmit={handleSubmit}
          >
            <Grid>
              <Card>
                <SeparateLabel label="Informações gerais" />

                <ContainerInline>
                  <Input name="name" label="Nome do exercício" />
                  <SelectField
                    clearValueChange
                    name="muscle_group"
                    label="Grupo muscular"
                    options={muscleGroups.map(group => ({
                      value: group.id as string,
                      label: group.name as string,
                    }))}
                    defaultValue={{ value: '' }}
                    placeholder="Grupo muscular"
                    onChange={handleChangeSelect}
                  />
                </ContainerInline>

                {listMuscleGroups.length > 0 &&
                  !listMuscleGroups.find(mg => mg.is_main) && (
                    <TextInfo text="Clique em um grupo muscular para torná-lo principal (usado para ordenação dos exercícios)." />
                  )}

                <ContainerCardsMuscleGroups>
                  {mainGroup && (
                    <CardMuscleGroup
                      key={mainGroup.id}
                      label={mainGroup.name as string}
                      isMain
                      onClickClose={() =>
                        setListMuscleGroups(groups => [
                          ...groups.filter(
                            current => current.id !== mainGroup.id,
                          ),
                        ])
                      }
                    />
                  )}
                  {secondaryGroups.map(group => (
                    <CardMuscleGroup
                      key={group.id}
                      label={group.name as string}
                      isMain={false}
                      onClickClose={() =>
                        setListMuscleGroups(groups => [
                          ...groups.filter(current => current.id !== group.id),
                        ])
                      }
                      onClickSetMain={() =>
                        setListMuscleGroups(groups =>
                          groups.map(current => ({
                            ...current,
                            is_main: current.id === group.id,
                          })),
                        )
                      }
                    />
                  ))}
                </ContainerCardsMuscleGroups>
              </Card>

              <Card>
                <SeparateLabel label="Instruções técnicas" />
                <InputTextArea
                  name="description"
                  label="Descrição"
                  placeholder="Explique como executar o exercício"
                />
              </Card>

              <Submit
                name="submit"
                value={isEditing ? 'Salvar exercício' : 'Criar exercício'}
                loading={loading}
                disabled={loading}
              />
            </Grid>
          </FormContainer>
        )}
      </Container>
    </Painel>
  );
};

export { ExerciseEditor };
